import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { cloneDeep } from 'lodash';
import { useSubscription } from '@apollo/client';
import Info from '../../../../../../components/channel-configuration/info';
import { InputSelect } from '../../../../../../components/input/select';
import { useBackdropContext } from '../../../../../../context/backdrop';
import {
  ChannelTypeInternal,
  OnSupplyReturnBehaviorChangeSubscription,
  SupplyReturnBehaviorInternal,
  OnSupplyReturnBehaviorChangeSubscriptionVariables,
} from '../../../../../../data-access/gql-types/graphql';
import { ON_SUPPLY_RETURN_BEHAVIOR_CHANGE } from '../../../../../../data-access/subscriptions/lavva-devices';
import { useTimeout } from '../../../../../../hooks/timeout/use-timeout';
import { toastSuccess } from '../../../../../../utils/toast';
import { useLightSupplyReturnBehaviour } from '../../../../api/light/light.hooks';
import { useSwitchSupplyReturnBehaviour } from '../../../../api/switch/switch.hooks';
import { useDevicesAndChannelsContext } from '../../../../context/devices-and-channels';
import { ChannelType, ChannelLightType, ChannelSwitchType } from '../../../../types';
import { SupplyReturnBehaviorRequest } from '../../../../types/graphql-rest-converter';

type ReturnBehaviorSelectProps = {
  channel: ChannelType;
};

const ReturnBehaviorSelect: React.FC<ReturnBehaviorSelectProps> = <T extends ChannelSwitchType | ChannelLightType>({
  channel,
}) => {
  const { t } = useTranslation('configuration');
  const { mutate: switchMutate } = useSwitchSupplyReturnBehaviour();
  const { mutate: lightMutate } = useLightSupplyReturnBehaviour();
  const { updateChannelDetails } = useDevicesAndChannelsContext();
  const { turnOnBackdrop, turnOffBackdrop } = useBackdropContext();
  const { setTimeoutError, clearTimeoutError } = useTimeout();
  const { data } = useSubscription<
    OnSupplyReturnBehaviorChangeSubscription,
    OnSupplyReturnBehaviorChangeSubscriptionVariables
  >(ON_SUPPLY_RETURN_BEHAVIOR_CHANGE, {
    variables: { channelId: (channel as ChannelType)?.id },
    skip: !channel,
  });

  useEffect(() => {
    if (data?.onSupplyReturnBehaviorChange) {
      updateChannelDetails((prev) => {
        if (prev?.id !== data.onSupplyReturnBehaviorChange.channelId) return prev;

        const temp = cloneDeep(prev);

        if (temp) {
          (temp.data as T).supplyReturnBehavior = data.onSupplyReturnBehaviorChange.supplyReturnBehavior;
        }

        return temp;
      });

      turnOffBackdrop();
      clearTimeoutError();
      toastSuccess({ content: t('returnBehavior.behaviorSaved') });
    }
  }, [data]);

  const onChangeReturnBehavior = (value: SupplyReturnBehaviorInternal) => {
    if (channel.data.type === ChannelTypeInternal.Switch) {
      turnOnBackdrop();

      switchMutate(
        { channelId: channel.id, deviceId: channel.deviceId, value: SupplyReturnBehaviorRequest[value] },
        {
          onSuccess: () => {
            setTimeoutError();
          },
        },
      );
    } else if (channel.data.type === ChannelTypeInternal.Light) {
      turnOnBackdrop();

      lightMutate(
        { channelId: channel.id, deviceId: channel.deviceId, value: SupplyReturnBehaviorRequest[value] },
        {
          onSuccess: () => {
            setTimeoutError();
          },
        },
      );
    }
  };

  const value = (channel.data as T)?.supplyReturnBehavior;

  return (
    <>
      <hr />
      <Info title={t('returnBehavior.label')} />
      <InputSelect
        value={value}
        onChange={onChangeReturnBehavior}
        options={[
          { label: t('returnBehavior.options.AlwaysOff'), value: SupplyReturnBehaviorInternal.AlwaysOff },
          { label: t('returnBehavior.options.AlwaysOn'), value: SupplyReturnBehaviorInternal.AlwaysOn },
          { label: t('returnBehavior.options.KeepState'), value: SupplyReturnBehaviorInternal.KeepState },
        ]}
        label={''}
      />
    </>
  );
};

export default ReturnBehaviorSelect;
